import { BrowserCacheLocation, InteractionRequiredAuthError, PublicClientApplication, type AccountInfo } from '@azure/msal-browser';
import { customerAppClientId } from '../authConfig';

/**
 * The customer's Global Admin, signed in through the multi-tenant "Customer Connect" app.
 *
 * A second MSAL instance, deliberately separate from the engineer's: different app, different
 * tenant, in-memory cache only (nothing about the customer persists in this browser after the
 * tab closes). Tokens are requested on demand and sent to our API per call; the API never
 * stores them.
 *
 * Two consents happen on first use in a tenant: Graph (sign-in, read principals, grant SPFx
 * API permissions) and then SharePoint (AllSites.FullControl on that tenant's host).
 */

export interface CustomerTokens { sharePoint?: string; graph?: string; sharePointHost?: string }

export interface CustomerConnection {
  tenantId: string;
  username: string;
  sharePointHost: string;          // https://{name}.sharepoint.com
}

const GRAPH_SCOPES = [
  'https://graph.microsoft.com/User.Read',
  'https://graph.microsoft.com/Application.Read.All',
  'https://graph.microsoft.com/DelegatedPermissionGrant.ReadWrite.All',
];
const spScopes = (host: string) => [`${host}/AllSites.FullControl`];

let pca: PublicClientApplication | null = null;
let account: AccountInfo | null = null;
let connection: CustomerConnection | null = null;

async function instance(): Promise<PublicClientApplication> {
  if (!pca) {
    pca = new PublicClientApplication({
      auth: { clientId: customerAppClientId, authority: 'https://login.microsoftonline.com/organizations',
              redirectUri: window.location.origin },
      cache: { cacheLocation: BrowserCacheLocation.MemoryStorage },
    });
    await pca.initialize();
  }
  return pca;
}

async function token(scopes: string[]): Promise<string> {
  const app = await instance();
  if (!account) throw new Error('The customer Global Admin is not signed in.');
  try {
    return (await app.acquireTokenSilent({ scopes, account })).accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) return (await app.acquireTokenPopup({ scopes, account })).accessToken;
    throw e;
  }
}

/** Derives the SharePoint host from the tenant's initial onmicrosoft.com domain. */
async function discoverSharePointHost(graphToken: string): Promise<string> {
  const res = await fetch('https://graph.microsoft.com/v1.0/organization?$select=verifiedDomains', {
    headers: { Authorization: `Bearer ${graphToken}` },
  });
  if (!res.ok) throw new Error('Could not read the tenant\'s domains.');
  const org = (await res.json()).value?.[0];
  const initial = org?.verifiedDomains?.find((d: { isInitial: boolean; name: string }) => d.isInitial)?.name as string | undefined;
  if (!initial?.endsWith('.onmicrosoft.com')) throw new Error('Could not determine the tenant\'s SharePoint address.');
  return `https://${initial.replace('.onmicrosoft.com', '').toLowerCase()}.sharepoint.com`;
}

/**
 * Opens the Microsoft sign-in popup for the customer's Global Admin, collects both consents and
 * returns the tenant they signed in to. The API independently refuses a tenant other than the
 * licensed one.
 */
export async function connectCustomer(loginHint?: string): Promise<CustomerConnection> {
  const app = await instance();
  const result = await app.loginPopup({ scopes: GRAPH_SCOPES, prompt: 'select_account', loginHint });
  account = result.account;
  const graph = await token(GRAPH_SCOPES);
  const host = await discoverSharePointHost(graph);
  await token(spScopes(host));                       // second consent, SharePoint
  connection = { tenantId: result.tenantId.toLowerCase(), username: result.account.username, sharePointHost: host };
  return connection;
}

export function currentCustomer(): CustomerConnection | null { return connection; }

export async function customerTokens(): Promise<CustomerTokens> {
  if (!connection) return {};
  return { graph: await token(GRAPH_SCOPES), sharePoint: await token(spScopes(connection.sharePointHost)),
           sharePointHost: connection.sharePointHost };
}

export async function disconnectCustomer(): Promise<void> {
  if (pca && account) await pca.clearCache({ account });
  account = null;
  connection = null;
}
